import { theme } from '@Guidelines/Theme/theme';
import type { NavigationMenuDestination } from '@UI/Routing/navigationMenu';
import { css } from '@emotion/react';
import type * as React from 'react';
import { NavLink } from 'react-router-dom';

const trackStyle = css`
	display: inline-flex;
	gap: 4px;
	padding: 4px;
	background: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.borderStrong};
	border-radius: ${theme.radii.pill};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		display: flex;
		flex: 1;
		width: 100%;
		box-sizing: border-box;
	}
`;

const segmentStyle = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	height: ${theme.controls.heightSmall};
	padding: 0 1rem;
	border-radius: ${theme.radii.pill};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.inkSecondary};
	text-decoration: none;
	white-space: nowrap;
	transition:
		background-color ${theme.motion.duration},
		color ${theme.motion.duration};

	&:hover {
		background: ${theme.colors.surfaceMuted};
		color: ${theme.colors.ink};
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}

	&[aria-current='page'] {
		background: ${theme.colors.accent};
		color: ${theme.colors.surface};
		font-weight: ${theme.fontWeights.semibold};
	}

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex: 1;
	}
`;

type Props = {
	readonly ariaLabel: string;
	readonly levels: readonly NavigationMenuDestination[];
};

/** Segmented control of links between the levels (account, organization, facility) of one menu entry. */
const LevelSwitch: React.FC<Props> = ({ ariaLabel, levels }) => {
	if (levels.length < 2) {
		return null;
	}

	return (
		<nav aria-label={ariaLabel} css={trackStyle}>
			{levels.map((level) => {
				const Icon = level.icon;

				return (
					<NavLink key={level.path} to={level.path} end css={segmentStyle}>
						<Icon size={16} aria-hidden="true" />
						{level.label}
					</NavLink>
				);
			})}
		</nav>
	);
};

export default LevelSwitch;
